import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Star } from "lucide-react";

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "fill-[#FFCC00] text-[#FFCC00]" : "text-neutral-300"}`} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { restaurant } = useAuth();
  const settingsSlug = restaurant?.slug;
  const { data: feedback = [] } = useQuery({ queryKey: ["feedback"], queryFn: async () => (await api.get("/feedback")).data });
  const avg = feedback.length ? (feedback.reduce((s, f) => s + f.rating, 0) / feedback.length).toFixed(1) : "—";
  const dist = [5, 4, 3, 2, 1].map((r) => ({ r, count: feedback.filter((f) => f.rating === r).length }));

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Customer Reviews" subtitle="Ratings & feedback from diners — linked to CRM profiles">
        <a href={settingsSlug ? `/r/${settingsSlug}/feedback` : "/feedback"} target="_blank" rel="noreferrer" className="text-sm text-[var(--kul-primary)]">Open feedback QR page</a>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="kul-card p-6 flex flex-col items-center justify-center">
          <div className="font-display text-5xl font-700 text-[var(--kul-primary)]" data-testid="avg-rating">{avg}</div>
          <Stars n={Math.round(Number(avg) || 0)} />
          <div className="text-sm text-neutral-500 mt-2">Average · {feedback.length} reviews</div>
        </div>
        <div className="lg:col-span-2 kul-card p-6 space-y-2">
          {dist.map((d) => (
            <div key={d.r} className="flex items-center gap-3 text-sm">
              <span className="w-4">{d.r}</span><Star className="w-3.5 h-3.5 fill-[#FFCC00] text-[#FFCC00]" />
              <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-[var(--kul-primary)]" style={{ width: feedback.length ? `${(d.count / feedback.length) * 100}%` : "0%" }} />
              </div>
              <span className="w-8 text-right text-neutral-400">{d.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {feedback.map((f) => (
          <div key={f.id} className="kul-card p-4" data-testid={`review-${f.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--kul-primary)]/10 text-[var(--kul-primary)] flex items-center justify-center font-semibold">{(f.name || "G")[0].toUpperCase()}</div>
                <div><div className="font-medium">{f.name || "Guest"}</div><div className="text-xs text-neutral-400">{f.phone} · {timeAgo(f.created_at)}</div></div>
              </div>
              <Stars n={f.rating} />
            </div>
            {f.comment && <p className="text-sm text-neutral-600 mt-3">"{f.comment}"</p>}
          </div>
        ))}
        {!feedback.length && <p className="text-neutral-400 text-center py-16">No reviews yet. They'll appear here after diners rate their orders.</p>}
      </div>
    </div>
  );
}
